import * as React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { AdminSideBar } from "@/components/SideBarAdmin";
import { WhiteContainer } from "@/styling/baseStyle";
import { Button, Combobox, Input, PaginationSimple, Popover, Spinner, Table, Tabs } from "@/components/ui";
import { ignorePopoverFocusOutOnChange } from "@/components/reviews";
import { cn } from "@/lib/utils";
import { useMfaChallenge } from "@/providers/MfaChallengeProvider";
import { UsersService } from "@/services";
import { AdminServices, StorageFile, StorageFilesResponse } from "@/services/bedrock";
import { URLS, useSocialCoachSession } from "@/utils";
import { ArrowDown, ArrowUp, ArrowUpDown, ListFilter } from "lucide-react";

type AccountKind = "coach" | "player";
type SortKey = "category" | "fileType" | "name" | "uploadedAt" | "size" | "path";

interface AccountMatch {
  kind: AccountKind;
  userId: number;
  label: string;
  detail?: string;
}

type FileSort = { key: SortKey; direction: "asc" | "desc" };

const FILE_TYPES = ["image", "video", "subtitle", "other"];
const PAGE_SIZE = 25;

const fileTypeLabel: Record<string, string> = {
  image: "Image",
  video: "Video",
  subtitle: "Subtitle",
  other: "Other",
};

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function formatUploadedAt(value: string | null) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function localDay(value: string | null) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value.slice(0, 10);
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

function userIdFromSearch(search: string): number | null {
  const id = Number(new URLSearchParams(search).get("userId"));
  return Number.isFinite(id) && id > 0 ? id : null;
}

function sameAccount(a: AccountMatch, b: AccountMatch) {
  return a.userId === b.userId;
}

function compareFiles(a: StorageFile, b: StorageFile, key: SortKey) {
  if (key === "size") return a.size - b.size;
  if (key === "uploadedAt") return (a.uploadedAt ?? "").localeCompare(b.uploadedAt ?? "");
  const left = key === "fileType" ? fileTypeLabel[a.fileType] ?? a.fileType : a[key] ?? "";
  const right = key === "fileType" ? fileTypeLabel[b.fileType] ?? b.fileType : b[key] ?? "";
  return left.localeCompare(right, undefined, { sensitivity: "base", numeric: true });
}

function accountKindLabel(kind: AccountKind) {
  return kind === "coach" ? "Coach" : "Player";
}

function UploadedFiles() {
  const history = useHistory();
  const location = useLocation();
  const linkedUserId = React.useMemo(() => userIdFromSearch(location.search), [location.search]);
  const [query, setQuery] = React.useState("");
  const [matches, setMatches] = React.useState<AccountMatch[]>([]);
  const [searching, setSearching] = React.useState(false);
  const [selected, setSelected] = React.useState<AccountMatch | null>(null);
  const [result, setResult] = React.useState<StorageFilesResponse | null>(null);
  const [loadingFiles, setLoadingFiles] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [categories, setCategories] = React.useState<string[]>([]);
  const [fileTypes, setFileTypes] = React.useState<string[]>([]);
  const [uploadedFrom, setUploadedFrom] = React.useState("");
  const [uploadedTo, setUploadedTo] = React.useState("");
  const [nameQuery, setNameQuery] = React.useState("");
  const [pathQuery, setPathQuery] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [sort, setSort] = React.useState<FileSort>({ key: "uploadedAt", direction: "desc" });

  React.useEffect(() => {
    const term = query.trim();
    if (term.length < 2) {
      setMatches([]);
      setSearching(false);
      return;
    }

    let cancelled = false;
    setSearching(true);
    UsersService.searchAccounts({ searchTerm: term, profileTypes: ["COACH", "PLAYER"] }, 0, 16)
      .then(accounts => {
        if (cancelled) return;
        setMatches(
          accounts.content.flatMap(account => {
            const userId = account.user.userId;
            const coachId = Number(account.coach?.id);
            const playerId = account.player?.id;
            const id = coachId || playerId;
            if (!userId || !id) return [];
            const kind: AccountKind = coachId ? "coach" : "player";
            const name =
              account.profile?.fullName ||
              [account.profile?.firstName, account.profile?.lastName].filter(Boolean).join(" ");
            const label = name || account.user.email || `${accountKindLabel(kind)} ${id}`;
            const email = account.user.email;
            return [
              {
                kind,
                userId,
                label,
                detail: email && email !== label ? email : undefined,
              },
            ];
          })
        );
      })
      .catch(() => {
        if (!cancelled) setMatches([]);
      })
      .finally(() => {
        if (!cancelled) setSearching(false);
      });

    return () => {
      cancelled = true;
    };
  }, [query]);

  const requestRef = React.useRef(0);
  const loadUser = React.useCallback(async (userId: number) => {
    const request = ++requestRef.current;
    setResult(null);
    setError(null);
    setCategories([]);
    setFileTypes([]);
    setUploadedFrom("");
    setUploadedTo("");
    setNameQuery("");
    setPathQuery("");
    setPage(1);
    setLoadingFiles(true);
    try {
      const files = await AdminServices.getStorageFiles(userId);
      if (request !== requestRef.current) return;
      const account = files.player ?? files.coach;
      const kind: AccountKind = files.player ? "player" : "coach";
      setSelected(current => {
        const label =
          account?.fullName ||
          (current?.userId === userId && current.label) ||
          `${accountKindLabel(kind)} ${account?.id ?? userId}`;
        const email = account?.email || (current?.userId === userId ? current.detail : undefined);
        return {
          kind,
          userId,
          label,
          detail: email && email !== label ? email : undefined,
        };
      });
      setResult(files);
    } catch {
      if (request !== requestRef.current) return;
      setError("Could not load files for this account.");
    } finally {
      if (request === requestRef.current) setLoadingFiles(false);
    }
  }, []);

  React.useEffect(() => {
    if (!linkedUserId) {
      requestRef.current += 1;
      setSelected(null);
      setResult(null);
      setError(null);
      setLoadingFiles(false);
      return;
    }
    loadUser(linkedUserId);
  }, [linkedUserId, loadUser]);

  const categoryOptions = React.useMemo(() => {
    const names = new Set((result?.files ?? []).map(file => file.category));
    return Array.from(names).sort();
  }, [result]);

  const filtered = React.useMemo(() => {
    const nameNeedle = nameQuery.trim().toLowerCase();
    const pathNeedle = pathQuery.trim().toLowerCase();
    return (result?.files ?? []).filter(file => {
      if (categories.length > 0 && !categories.includes(file.category)) return false;
      if (fileTypes.length > 0 && !fileTypes.includes(file.fileType)) return false;
      const uploadedDay = localDay(file.uploadedAt);
      if (uploadedFrom && (!uploadedDay || uploadedDay < uploadedFrom)) return false;
      if (uploadedTo && (!uploadedDay || uploadedDay > uploadedTo)) return false;
      if (nameNeedle && !file.name.toLowerCase().includes(nameNeedle)) return false;
      if (pathNeedle && !file.path.toLowerCase().includes(pathNeedle)) return false;
      return true;
    });
  }, [result, categories, fileTypes, uploadedFrom, uploadedTo, nameQuery, pathQuery]);

  React.useEffect(() => {
    setPage(1);
  }, [categories, fileTypes, uploadedFrom, uploadedTo, nameQuery, pathQuery, sort]);

  const sorted = React.useMemo(() => {
    const next = [...filtered];
    next.sort((a, b) => {
      const compared = compareFiles(a, b, sort.key);
      return sort.direction === "asc" ? compared : -compared;
    });
    return next;
  }, [filtered, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const pageRows = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const accountOptions =
    selected && !matches.some(match => sameAccount(match, selected)) ? [selected, ...matches] : matches;

  const toggleSort = (key: SortKey) => {
    setSort(current =>
      current.key === key
        ? { key, direction: current.direction === "asc" ? "desc" : "asc" }
        : { key, direction: key === "uploadedAt" || key === "size" ? "desc" : "asc" }
    );
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="w-full md:max-w-md">
        <Combobox<AccountMatch>
          searchable
          showClear
          items={accountOptions}
          value={selected}
          onSearch={setQuery}
          onValueChange={account => {
            setQuery("");
            setSelected(account);
            const nextSearch = account ? `userId=${account.userId}` : "";
            if (nextSearch === location.search.replace(/^\?/, "")) return;
            history.replace({ pathname: location.pathname, search: nextSearch });
          }}
          isItemEqualToValue={sameAccount}
          itemToStringLabel={item => `${item.label} · ${accountKindLabel(item.kind)}`}
          itemToLabel={item => (
            <span className="flex min-w-0 flex-col">
              <span className="truncate">{item.label}</span>
              {item.detail && <span className="truncate text-xs text-muted-foreground">{item.detail}</span>}
            </span>
          )}
          itemToBadge={item => accountKindLabel(item.kind)}
          placeholder="Search coaches and players..."
          emptyText={searching || query.trim().length >= 2 ? "No matches" : "Type to search"}
          isLoadingMore={searching}
        />
      </div>

      {loadingFiles && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Spinner className="size-4" />
          Loading files
        </div>
      )}
      {error && <div className="text-sm text-destructive">{error}</div>}

      <div className="w-full overflow-hidden rounded-xl border border-slate-200/60 bg-background shadow-xs">
        <Table className="w-full text-sm">
          <Table.Header>
            <Table.Row>
              <ColumnHeader
                label="Category"
                column="category"
                sort={sort}
                onSort={toggleSort}
                onClear={categories.length > 0 ? () => setCategories([]) : undefined}
              >
                <Combobox
                  multiple
                  items={categoryOptions}
                  value={categories}
                  onValueChange={setCategories}
                  itemToStringLabel={item => item}
                  placeholder="Category"
                />
              </ColumnHeader>
              <ColumnHeader
                label="Type"
                column="fileType"
                sort={sort}
                onSort={toggleSort}
                onClear={fileTypes.length > 0 ? () => setFileTypes([]) : undefined}
              >
                <Combobox
                  multiple
                  items={FILE_TYPES}
                  value={fileTypes}
                  onValueChange={setFileTypes}
                  itemToStringLabel={item => fileTypeLabel[item] ?? item}
                  placeholder="File type"
                />
              </ColumnHeader>
              <ColumnHeader
                label="File"
                column="name"
                sort={sort}
                onSort={toggleSort}
                onClear={nameQuery.trim() ? () => setNameQuery("") : undefined}
              >
                <Input
                  value={nameQuery}
                  placeholder="Search file name"
                  onChange={event => setNameQuery(event.target.value)}
                />
              </ColumnHeader>
              <ColumnHeader
                label="Uploaded"
                column="uploadedAt"
                sort={sort}
                onSort={toggleSort}
                onClear={
                  uploadedFrom || uploadedTo
                    ? () => {
                        setUploadedFrom("");
                        setUploadedTo("");
                      }
                    : undefined
                }
              >
                <div className="flex flex-col gap-2">
                  <DateFilter label="From" value={uploadedFrom} onChange={setUploadedFrom} />
                  <DateFilter label="To" value={uploadedTo} onChange={setUploadedTo} />
                </div>
              </ColumnHeader>
              <ColumnHeader label="Size" column="size" sort={sort} onSort={toggleSort} />
              <ColumnHeader
                label="Path"
                column="path"
                sort={sort}
                onSort={toggleSort}
                onClear={pathQuery.trim() ? () => setPathQuery("") : undefined}
              >
                <Input
                  value={pathQuery}
                  placeholder="Search path"
                  onChange={event => setPathQuery(event.target.value)}
                />
              </ColumnHeader>
              <Table.Head />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {pageRows.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={7} className="text-muted-foreground">
                  {loadingFiles
                    ? "Loading files"
                    : selected
                    ? "No files match these filters."
                    : "Select a Coach or Player to see their files."}
                </Table.Cell>
              </Table.Row>
            ) : (
              pageRows.map(file => <FileRow key={file.path} file={file} />)
            )}
          </Table.Body>
        </Table>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {!result || sorted.length === result.files.length
            ? `${sorted.length.toLocaleString()} ${sorted.length === 1 ? "file" : "files"}`
            : `${sorted.length.toLocaleString()} of ${result.files.length.toLocaleString()} files`}
        </p>
        {sorted.length > 0 && <PaginationSimple page={page} totalPages={totalPages} onPageChange={setPage} />}
      </div>
    </div>
  );
}

function DateFilter({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <label className="flex flex-col gap-1 text-xs text-muted-foreground">
      {label}
      <input
        type="date"
        value={value}
        aria-label={`Uploaded ${label.toLowerCase()}`}
        onChange={event => onChange(event.currentTarget.value)}
        className={cn(
          "h-9 w-full rounded-lg border border-input bg-white px-2.5 text-sm text-foreground",
          !value && "[&::-webkit-datetime-edit]:text-transparent"
        )}
      />
    </label>
  );
}

function ColumnHeader({
  label,
  column,
  sort,
  onSort,
  onClear,
  children,
}: {
  label: string;
  column: SortKey;
  sort: FileSort;
  onSort: (column: SortKey) => void;
  onClear?: () => void;
  children?: React.ReactNode;
}) {
  const [open, setOpen] = React.useState(false);
  const active = sort.key === column;
  const SortIcon = active ? (sort.direction === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;

  return (
    <Table.Head>
      <div className="flex items-center gap-1">
        <button
          type="button"
          className="group inline-flex cursor-pointer items-center gap-1 font-medium whitespace-nowrap"
          onClick={() => onSort(column)}
          aria-label={`Sort by ${label}`}
        >
          <SortIcon
            className={cn("size-3.5 shrink-0", active ? "text-primary" : "text-slate-400 group-hover:text-inherit")}
            strokeWidth={active ? 3 : 2}
          />
          {label}
        </button>
        {children ? (
          <Popover open={open} onOpenChange={ignorePopoverFocusOutOnChange(setOpen)}>
            <Popover.Trigger
              render={
                <Button
                  type="button"
                  variant={onClear ? "default" : "ghost"}
                  size="icon-sm"
                  className="size-6"
                  aria-label={onClear ? `Filter ${label} (active)` : `Filter ${label}`}
                  aria-pressed={Boolean(onClear)}
                >
                  <ListFilter className="size-3.5" />
                </Button>
              }
            />
            <Popover.Content
              align="start"
              side="bottom"
              sideOffset={6}
              initialFocus={false}
              className="w-72 max-w-[calc(100vw-2rem)] gap-3 border border-slate-100 bg-white p-3 shadow-xl"
            >
              <div className="flex min-h-8 items-center justify-between gap-2">
                <span className="text-xs font-bold tracking-wider text-slate-500 uppercase">{label}</span>
                {onClear ? (
                  <Button variant="ghost" size="sm" className="h-7 shrink-0 px-2 text-xs" onClick={onClear}>
                    Clear
                  </Button>
                ) : null}
              </div>
              {children}
            </Popover.Content>
          </Popover>
        ) : null}
      </div>
    </Table.Head>
  );
}

function FileRow({ file }: { file: StorageFile }) {
  return (
    <Table.Row>
      <Table.Cell>{file.category}</Table.Cell>
      <Table.Cell>{fileTypeLabel[file.fileType] ?? file.fileType}</Table.Cell>
      <Table.Cell className="max-w-48 truncate">{file.name}</Table.Cell>
      <Table.Cell className="whitespace-nowrap">{formatUploadedAt(file.uploadedAt)}</Table.Cell>
      <Table.Cell className="whitespace-nowrap">{formatSize(file.size)}</Table.Cell>
      <Table.Cell className="max-w-xs truncate" title={file.path}>
        {file.path}
      </Table.Cell>
      <Table.Cell>
        {file.url && (
          <a className="text-primary underline" href={file.url} target="_blank" rel="noreferrer">
            Open
          </a>
        )}
      </Table.Cell>
    </Table.Row>
  );
}

export function ToolsPage() {
  const history = useHistory();
  const { session } = useSocialCoachSession();
  const { triggerMfa, mfaVerified } = useMfaChallenge();
  const [allowed, setAllowed] = React.useState(mfaVerified);
  const prompted = React.useRef(false);

  React.useEffect(() => {
    if (mfaVerified) {
      setAllowed(true);
      return;
    }
    if (!session) return;
    if (prompted.current) return;
    prompted.current = true;
    triggerMfa()
      .then(() => setAllowed(true))
      .catch(() => history.replace(URLS.admin.dashboard));
  }, [session, mfaVerified, triggerMfa, history]);

  return (
    <WhiteContainer>
      <AdminSideBar history={history} />
      <div className="hugePadding adminUserTabs flex-1 w-full min-w-0">
        <Tabs defaultValue="uploaded-files" className="w-full">
          <Tabs.List variant="menu" className="mb-4">
            <Tabs.Trigger value="uploaded-files" className="text-sm font-semibold">
              Uploaded files
            </Tabs.Trigger>
          </Tabs.List>
          <Tabs.Content value="uploaded-files">
            <div className="pt-2">{allowed && <UploadedFiles />}</div>
          </Tabs.Content>
        </Tabs>
      </div>
    </WhiteContainer>
  );
}
